import React from 'react';
import { Pressable, View, useWindowDimensions } from 'react-native';
import { useColors } from '../theme';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { T } from './Text';

export function SectionTitle({
  title,
  icon,
  hint,
  hintIcon,
  onHintPress,
  hintColor,
  first = false,
}: {
  title: string;
  icon?: IconName;
  hint?: string;
  hintIcon?: IconName;
  onHintPress?: () => void;
  hintColor?: string;
  /** Drops the top margin when this is the first thing on a screen. */
  first?: boolean;
}) {
  const colors = useColors();
  const { width } = useWindowDimensions();
  // Side by side, a long hint wins the row and squeezes the heading — "Select
  // a table" was breaking across two lines next to "Each table keeps its own
  // running order", which had the whole rest of the row to itself. On a phone
  // the hint drops underneath instead, where it has the full width.
  //
  // A tappable hint is a control, not prose, so it stays on the row: it is
  // short ("Orders ›", "Rename these"), and moving it would leave the heading
  // looking like it had lost its button.
  const stack = width < 560 && !!hint && !onHintPress;
  const tint = hintColor ?? colors.textFaint;
  const hintNode = hint ? (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      {hintIcon ? <Icon name={hintIcon} size={13} color={tint} /> : null}
      <T size={12} color={tint} weight={onHintPress ? 700 : 400}>
        {hint}
      </T>
    </View>
  ) : null;

  return (
    <View
      style={{
        flexDirection: stack ? 'column' : 'row',
        alignItems: stack ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        marginTop: first ? 0 : 26,
        marginBottom: 12,
        gap: stack ? 4 : 12,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 9,
          flexShrink: 1,
          // On one row the heading may shrink; stacked it owns the full width
          // and must not be squeezed by a sibling that is no longer beside it.
          ...(stack ? { alignSelf: 'stretch' } : null),
        }}
      >
        {icon ? <Icon name={icon} size={16} color={colors.gold} /> : null}
        <T size={15.5} weight={600} serif style={{ flexShrink: 1 }}>
          {title}
        </T>
      </View>
      {onHintPress ? <Pressable onPress={onHintPress}>{hintNode}</Pressable> : hintNode}
    </View>
  );
}
